import {
  CLOSE_OUTDATED,
  CLOSE_REPLACED,
  OUTDATED_RETRY_MS,
  parseServerMessage,
  sendMessage,
  type ClientMessage,
  type OnlineRoom,
  type ServerMessage,
} from "@golazo/shared";

import { CLIENT_SIM_VERSION } from "./version.js";

/**
 * `elsewhere`: la cuenta se ha abierto en otra pestaña o dispositivo y el
 * servidor se ha quedado con esa conexion. Aqui NO se reconecta solo (ver
 * `CLOSE_REPLACED`): se vuelve con `connect()`, que es el "Jugar aqui".
 *
 * `outdated`: este cliente y el servidor traen simulaciones distintas (ver
 * `CLOSE_OUTDATED`). Hay que recargar la pagina; mientras, se reintenta cada
 * `OUTDATED_RETRY_MS` por si el que iba por detras era el servidor.
 */
export type ConnectionState =
  | "offline"
  | "connecting"
  | "online"
  | "reconnecting"
  | "elsewhere"
  | "outdated";

const CLIENT_ID_KEY = "golazo.clientId";
/** Credencial: sin ella el server emite una identidad nueva. */
const TOKEN_KEY = "golazo.token";

type MessageListener = (msg: ServerMessage) => void;
type StateListener = (state: ConnectionState) => void;

export interface OnlineClientOptions {
  url: string;
  clientId?: string;
  /**
   * El apodo que se PIDE en cada `hello`. Funcion y no valor: se lee al
   * conectar, y si el jugador lo cambio mientras tanto, reconectar pide el
   * nuevo. Vacio = "el que ya tenga mi cuenta".
   */
  nickname?: string | (() => string);
  reconnect?: boolean;
}

export interface OnlineClient {
  readonly clientId: string;
  readonly state: ConnectionState;
  /** RTT medido en ms (-1 si aun no hay medida). */
  readonly rtt: number;
  /**
   * Sala que este cliente esta jugando. Se manda en cada `hello` para que el
   * server pueda decirle, al reconectar, que esa partida ya no existe.
   */
  activeRoomId: string | null;
  connect(): Promise<void>;
  disconnect(): void;
  send(msg: ClientMessage): void;
  onMessage(fn: MessageListener): () => void;
  onState(fn: StateListener): () => void;
  /**
   * Bundles de la sala que llegaron hace poco, en orden. Ver `RECENT_BUNDLES`.
   * Opcional para que los clientes falsos de los tests no tengan que darlo.
   */
  recentBundles?(roomId: string): ReadonlyArray<TickBundle>;
  dispose(): void;
}

type TickBundle = Extract<ServerMessage, { type: "tick:bundle" }>;

/**
 * Cuantos bundles se guardan por si el lockstep aun no existe cuando llegan.
 *
 * El servidor arranca el reloj en el mismo instante en que manda el
 * `match:latejoin`, y el lockstep de quien entra se crea DESPUES, al volver del
 * lobby. Hoy ese camino no tiene ningun `await` y el lockstep se crea en la
 * misma tarea, pero basta uno (o un WebView que agrupe mensajes) para que el
 * primer bundle caiga en el vacio: al lockstep le falta ese tick, no avanza
 * nunca y el que entra ve el partido congelado sin ningun error. Dos segundos
 * cubren de sobra cualquier transicion de pantalla.
 */
const RECENT_BUNDLES = 120;

/*
 * Token y id viven en `localStorage`, NO en `sessionStorage`.
 *
 * Con `sessionStorage` la identidad moria al cerrar la pestaña, asi que el
 * jugador volvia siendo otro y perdia su carrera. Eso convertia el token en un
 * mecanismo de reconexion, no en una cuenta. El server ya lo guarda en SQLite;
 * esta es la otra mitad.
 *
 * `localStorage` es por origen y no caduca solo. En navegacion privada o con el
 * almacenamiento bloqueado, los `catch` dejan al jugador como invitado de un
 * solo uso: se juega igual, solo que sin carrera. Un adorno no puede impedir
 * entrar.
 */
function loadClientId(fallback?: string): string {
  try {
    return localStorage.getItem(CLIENT_ID_KEY) ?? fallback ?? uid();
  } catch {
    return fallback ?? uid();
  }
}

function saveClientId(id: string): void {
  try {
    localStorage.setItem(CLIENT_ID_KEY, id);
  } catch {
    // Sin almacenamiento: invitado de un solo uso.
  }
}

function loadToken(): string | undefined {
  try {
    return localStorage.getItem(TOKEN_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

function saveToken(token: string): void {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch {
    // Sin almacenamiento: invitado de un solo uso.
  }
}

function uid(): string {
  return Math.random().toString(36).slice(2, 9);
}

export function createOnlineClient(options: OnlineClientOptions): OnlineClient {
  const reconnectEnabled = options.reconnect !== false;
  let clientId = loadClientId(options.clientId);
  let token = loadToken();
  /*
   * Hasta el 2026-09-26 aqui habia un "Jugador" fijo y nadie pasaba otro: cada
   * conexion le decia al servidor que te llamabas "Jugador", y el servidor lo
   * escribia en tu cuenta. Ahora se pide el que tengas guardado, o nada.
   */
  const nickname = (): string =>
    typeof options.nickname === "function" ? options.nickname() : (options.nickname ?? "");
  let ws: WebSocket | null = null;
  let state: ConnectionState = "offline";
  let disposed = false;
  let reconnectTimer = 0;
  let reconnectAttempt = 0;
  let connectPromise: Promise<void> | null = null;
  let rtt = -1;
  let pingTimer = 0;
  let activeRoomId: string | null = null;
  let recent: TickBundle[] = [];

  const messageListeners = new Set<MessageListener>();
  const stateListeners = new Set<StateListener>();

  const setState = (next: ConnectionState): void => {
    if (state === next) return;
    state = next;
    for (const fn of stateListeners) fn(state);
  };

  const scheduleReconnect = (): void => {
    if (!reconnectEnabled || disposed) return;
    setState("reconnecting");
    const delay = Math.min(1000 * 2 ** reconnectAttempt, 10000);
    reconnectAttempt += 1;
    clearTimeout(reconnectTimer);
    reconnectTimer = window.setTimeout(() => {
      connectPromise = null;
      void connectInternal();
    }, delay);
  };

  const startPings = (socket: WebSocket): void => {
    clearInterval(pingTimer);
    // Uno en el acto: el margen del lockstep se calcula con el RTT, y sin medida
    // cae al valor prudente (6 ticks, 100 ms de retraso al pulsar) durante los
    // primeros 3 s — justo lo que dura entrar en un partido desde el lobby.
    sendMessage(socket, { type: "ping", t: Date.now() });
    pingTimer = window.setInterval(() => {
      if (socket.readyState !== WebSocket.OPEN) {
        clearInterval(pingTimer);
        return;
      }
      sendMessage(socket, { type: "ping", t: Date.now() });
    }, 3000);
  };

  const connectInternal = (): Promise<void> => {
    if (connectPromise) return connectPromise;

    connectPromise = new Promise((resolve, reject) => {
      if (disposed) {
        reject(new Error("Client disposed"));
        return;
      }
      if (isOpen(ws)) {
        setState("online");
        resolve();
        return;
      }

      // Reintentando tras un `CLOSE_OUTDATED` no se enseña "conectando": el
      // aviso de version nueva se queda hasta que el servidor de la bienvenida.
      if (state !== "outdated") setState(reconnectAttempt > 0 ? "reconnecting" : "connecting");
      const socket = new WebSocket(options.url);

      socket.addEventListener("open", () => {
        ws = socket;
        // El token es lo que reclama la identidad; el clientId es publico y el
        // server lo ignora a proposito.
        sendMessage(socket, {
          type: "hello",
          token,
          nickname: nickname(),
          roomId: activeRoomId ?? undefined,
          version: CLIENT_SIM_VERSION,
        });
      });

      socket.addEventListener("message", (ev) => {
        const raw = typeof ev.data === "string" ? ev.data : String(ev.data);
        const msg = parseServerMessage(raw);
        if (!msg) return;

        if (msg.type === "welcome") {
          clientId = msg.clientId;
          token = msg.token;
          saveClientId(clientId);
          saveToken(token);
          reconnectAttempt = 0;
          setState("online");
          startPings(socket);
          resolve();
        }

        if (msg.type === "account:redeemed") {
          // Canje aceptado: a partir de aqui ESTE navegador es esa cuenta. Sin
          // guardar el token nuevo, la recuperacion duraba hasta recargar y el
          // jugador volvia a su cuenta de invitado con la carrera perdida otra
          // vez — que es justo lo que se venia a arreglar.
          clientId = msg.clientId;
          token = msg.token;
          saveClientId(clientId);
          saveToken(token);
        }

        if (msg.type === "pong") {
          // Media movil: una medida suelta salta demasiado para decidir nada.
          const sample = Date.now() - msg.t;
          rtt = rtt < 0 ? sample : rtt * 0.8 + sample * 0.2;
        }

        // Un partido nuevo (tambien una revancha en la misma sala) empieza a
        // contar desde su tick: lo guardado del anterior tendria numeros
        // validos y se colaria. Estos dos mensajes van SIEMPRE por delante de
        // sus bundles en el socket, asi que vaciar aqui es seguro.
        if (msg.type === "match:start" || msg.type === "match:latejoin") recent = [];
        if (msg.type === "tick:bundle") {
          if (recent.length > 0 && recent[0]!.roomId !== msg.roomId) recent = [];
          recent.push(msg);
          if (recent.length > RECENT_BUNDLES) recent.shift();
        }

        for (const fn of messageListeners) fn(msg);
      });

      socket.addEventListener("close", (ev) => {
        // Un socket viejo que cierra tarde no manda sobre el que ya esta vivo.
        if (ws !== null && ws !== socket) return;
        ws = null;
        connectPromise = null;
        clearInterval(pingTimer);
        if (disposed) return;
        if (ev.code === CLOSE_REPLACED) {
          // Reconectar aqui echaria a la otra, que haria lo mismo: un bucle.
          clearTimeout(reconnectTimer);
          setState("elsewhere");
          return;
        }
        if (ev.code === CLOSE_OUTDATED) {
          // Llega ANTES de la bienvenida: quien espera a `connect()` se
          // quedaria colgado para siempre si no se le contesta.
          reject(new Error("outdated"));
          setState("outdated");
          clearTimeout(reconnectTimer);
          if (reconnectEnabled) {
            reconnectTimer = window.setTimeout(() => {
              connectPromise = null;
              void connectInternal().catch(() => {});
            }, OUTDATED_RETRY_MS);
          }
          return;
        }
        setState("offline");
        scheduleReconnect();
      });

      socket.addEventListener("error", () => {
        connectPromise = null;
        if (state === "connecting") reject(new Error("WebSocket connection failed"));
      });
    });

    return connectPromise;
  };

  function isOpen(socket: WebSocket | null): socket is WebSocket {
    return socket !== null && socket.readyState === WebSocket.OPEN;
  }

  return {
    get clientId() {
      return clientId;
    },
    get state() {
      return state;
    },
    get rtt() {
      return rtt;
    },
    get activeRoomId() {
      return activeRoomId;
    },
    set activeRoomId(next: string | null) {
      activeRoomId = next;
    },
    async connect() {
      clearTimeout(reconnectTimer);
      reconnectAttempt = 0;
      return connectInternal();
    },
    disconnect() {
      clearTimeout(reconnectTimer);
      clearInterval(pingTimer);
      disposed = true;
      connectPromise = null;
      ws?.close();
      ws = null;
      setState("offline");
    },
    send(msg: ClientMessage) {
      if (!isOpen(ws)) return;
      sendMessage(ws, msg);
    },
    onMessage(fn: MessageListener) {
      messageListeners.add(fn);
      return () => messageListeners.delete(fn);
    },
    onState(fn: StateListener) {
      stateListeners.add(fn);
      fn(state);
      return () => stateListeners.delete(fn);
    },
    recentBundles(roomId: string) {
      return recent.filter((b) => b.roomId === roomId);
    },
    dispose() {
      this.disconnect();
      messageListeners.clear();
      stateListeners.clear();
    },
  };
}

export type { OnlineRoom };
